import { useEffect } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { ScrollToTopButton, Container, Stack, PageHeader, ProgressSpinner } from '@/components/ui';
import { MainLayout } from '@/layouts/MainLayout';
import { RecentFixturesList } from '@/components/fixtures/RecentFixturesList';
import { FixtureSkeleton } from '@/components/fixtures/FixtureSkeleton';
import { FixtureStatus } from '@/components/fixtures/FixtureStatus';
import { VesselSizeFilter } from '@/components/fixtures/VesselSizeFilter';
import { useRecentFixturesStore } from '@/store/recentFixturesStore';

export default function RecentFixturesPage() {
  const {
    fixtures,
    groupedFixtures,
    loading,
    error,
    refreshing,
    filters,
    fetchFixtures,
    refreshFixtures,
    clearError,
    toggleOnSubsFilter,
    toggleOwnFixtureFilter,
    setVesselSizeFilter,
    fetchVesselSizeAccess,
    disallowedVesselSizeIds,
  } = useRecentFixturesStore();

  useEffect(() => {
    fetchFixtures();
    fetchVesselSizeAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => {
    clearError();
    refreshFixtures();
  };

  const title = fixtures.length > 0 ? `Recent Fixtures (${fixtures.length})` : 'Recent Fixtures';

  return (
    <MainLayout>
      <Container className="pb-8">
        <Stack direction="vertical" spacing={6} className="recent-fixtures-page">
          <PageHeader title={title} />

          {/* Filter toolbar — vessel size on its own row, status toggles + refresh below */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <VesselSizeFilter
              variant="segmented"
              value={filters.vesselSizeId ?? 0}
              onChange={setVesselSizeFilter}
              disallowedVesselSizeIds={disallowedVesselSizeIds}
              className="w-full sm:w-auto"
            />

            <div className="flex items-center gap-2 sm:gap-3">
              <label className="flex shrink-0 cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={filters.isOnSubs}
                  onChange={toggleOnSubsFilter}
                  className="sr-only"
                />
                <div
                  className={`relative h-5 w-9 rounded-full transition-colors ${
                    filters.isOnSubs ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <div
                    className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
                      filters.isOnSubs ? 'translate-x-4' : ''
                    }`}
                  />
                </div>
                <FixtureStatus status="subs" className="whitespace-nowrap text-xs sm:text-sm" />
              </label>

              <label className="flex shrink-0 cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={filters.isOwnFixture}
                  onChange={toggleOwnFixtureFilter}
                  className="sr-only"
                />
                <div
                  className={`relative h-5 w-9 rounded-full transition-colors ${
                    filters.isOwnFixture ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <div
                    className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
                      filters.isOwnFixture ? 'translate-x-4' : ''
                    }`}
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <img src="/assets/images/tankers-uk.svg" alt="TI Fixture" className="h-5 w-5" />
                  <span className="whitespace-nowrap text-xs font-semibold text-gray-700 dark:text-gray-200 sm:text-sm">
                    TI Fixtures
                  </span>
                </div>
              </label>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                title="Refresh"
                aria-label="Refresh"
                className="!min-h-9 !min-w-9 shrink-0 !px-2 !py-2"
              >
                {refreshing ? <ProgressSpinner size="sm" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {error && (
            <Alert variant="error" className="flex items-center justify-between">
              <div>
                <p className="font-medium">Error loading recent fixtures</p>
                <p className="text-sm opacity-90">{error}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={clearError}>
                  Dismiss
                </Button>
                <Button size="sm" onClick={handleRefresh}>
                  Try Again
                </Button>
              </div>
            </Alert>
          )}

          {loading && fixtures.length === 0 ? (
            <FixtureSkeleton />
          ) : fixtures.length === 0 && !loading ? (
            <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
              <div className="py-8 text-center">
                <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-gray-100 p-3 dark:bg-gray-700">
                  <Search className="h-6 w-6 text-gray-400" />
                </div>
                <h3 className="mb-2 text-base font-medium text-gray-900 dark:text-white">
                  No recent fixtures found
                </h3>
                <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                  {filters.isOnSubs || filters.isOwnFixture
                    ? 'No fixtures match your current filters. Try adjusting or clearing them.'
                    : 'No recent fixtures are available at the moment. Try refreshing to load the latest data.'}
                </p>
                <Button onClick={handleRefresh} disabled={refreshing}>
                  {refreshing ? 'Refreshing...' : 'Refresh Data'}
                </Button>
              </div>
            </div>
          ) : (
            <RecentFixturesList groupedFixtures={groupedFixtures} loading={loading} />
          )}

          <ScrollToTopButton />
        </Stack>
      </Container>
    </MainLayout>
  );
}
